// web/src/store/phone/_session.ts —— 内部：客户匹配 / 会话清理 / CDR 基线 / 事件绑定 / 资源回收
// 拆自 web/src/store/phone.ts 的同名分区（内容原样搬运）；由 web/src/store/phone.ts 以 export * 聚合，导出面对调用方不变。

import { Invitation, Inviter, Registerer, RegistererState, Session, SessionState, TransportState, UserAgent, UserAgentRegisteredOptionTags } from 'sip.js';
import { getCustomerByCall, listCustomers, type Customer } from '../../api/crm';
import { getCustomerProfile } from '../../api/assist';
import { listCdrs } from '../../api/cdr';
import { useAgentStore } from '../agent';
import { requestNotifyPermission, startRingAlert, stopRingAlert } from '../../utils/ringAlert';

import { setupRemoteAudio } from './_audio';
import { parseUser } from './_env';
import { INITIAL } from './_model';
import { rt } from './_state';
import { scheduleOutboundEcho } from './_zitch';
import { normalizePhone } from './phone-number';
import { usePhoneStore } from './store';


/**
 * 按号码匹配客户语境（静默失败）：CRM 命中写入 customer（服务记录/工单用），
 * 电话条展示名单独取「外部业务档案优先、CRM 兜底」的合并来源（后端 /assist/customer-profile）。
 *
 * 号码口径是**软电话对端号**（这通电话从哪个号打进来），用于振铃即弹屏；
 * 通话中客户身份可能变（身份核验通过 / 坐席「收号管理」采集到客户手机号），
 * 那时改用 matchCallCustomer 按呼叫上下文重新解析。
 */
export async function matchCustomer(number: string) {
  if (!number) {
    usePhoneStore.setState({ customer: null, displayName: '' });
    return;
  }
  const phone = normalizePhone(number);
  let customer: Customer | null = null;
  try {
    // 归一化后查询（后端 List 亦做 IN 变体重查，双保险）
    const { list } = await listCustomers({ phone, page: 1, pageSize: 1 });
    customer = list[0] || null;
    usePhoneStore.setState({ customer });
  } catch {
    /* 静默 */
  }
  try {
    const profile = await getCustomerProfile(phone);
    usePhoneStore.setState({ displayName: (profile?.name || customer?.name || '').trim() });
  } catch {
    /* 静默 */
  }
}

/**
 * 按**呼叫上下文**解析本通话客户（静默失败）：号码由后端 `/customers/by-call` 按
 * 「核验通过号 > 本通已收号手机号 > 呼叫方向号 > 软电话对端号」选出明文返回——
 * 前端拿不到收号插槽的明文（插槽接口与 WS 事件一律脱敏），故这一步只能由后端定号。
 *
 * 写入 customer（服务记录/工单用）与 customerPhone（客户运营区画像取数用，见 ops/useOpsProfile）。
 * 展示名只在**号码变了**时才按画像重取（解析结果与对端号相同即沿用振铃那一次的结果），
 * 否则每次解析都白打一次画像上游。归属校验拒绝/异常时保持既有语境不动：宁可用对端号，
 * 也不要把客户清空。
 */
export async function matchCallCustomer(uuid: string, number: string) {
  const fallback = normalizePhone(number);
  try {
    const r = await getCustomerByCall({ uuid, phone: fallback });
    const phone = normalizePhone(r?.phone || '') || fallback;
    const prev = usePhoneStore.getState();
    usePhoneStore.setState({ customer: r?.customer || null, customerPhone: phone });
    if (phone && phone !== prev.customerPhone && phone !== prev.remoteNumber) {
      const profile = await getCustomerProfile(phone);
      usePhoneStore.setState({ displayName: (profile?.name || r?.customer?.name || '').trim() });
    }
  } catch {
    /* 静默 */
  }
}

export function cleanupSession() {
  rt.session = null;
  stopRingAlert();
  if (rt.audio) rt.audio.srcObject = null;
  const prev = usePhoneStore.getState();
  const elapsed = prev.startedAt ? Math.max(0, Date.now() - prev.startedAt) : undefined;
  // customer/customerPhone/cdrBaseline 保留（话后处理、服务小结定位），下一通电话到来时覆盖
  usePhoneStore.setState((p) => ({
    ...INITIAL,
    extension: p.extension,
    engine: p.engine,
    customer: p.customer,
    customerPhone: p.customerPhone,
    displayName: p.displayName,
    cdrBaseline: p.cdrBaseline,
    // 同上：盲转标记不清，留待消费方（小结触发/话后快照）读取后复位
    transferredOut: p.transferredOut,
    status: rt.ua?.isConnected() ? 'registered' : p.status === 'error' ? 'error' : 'idle',
  }));
  scheduleOutboundEcho(elapsed);
}

/** 响铃时快照本人最新 CDR id 作为小结定位基线（静默失败：SummaryModal 退化为时间窗校验） */
export async function captureCdrBaseline() {
  try {
    const agentId = useAgentStore.getState().agentId;
    if (!agentId) return;
    const { list } = await listCdrs({ agent_id: agentId, page: 1, pageSize: 1 });
    usePhoneStore.setState({ cdrBaseline: list[0]?.id ?? 0 });
  } catch {
    /* 静默 */
  }
}

export function bindSession(s: Session, direction: 'in' | 'out') {
  rt.session = s;
  setupRemoteAudio(s);
  s.stateChange.addListener((st) => {
    if (st === SessionState.Establishing || st === SessionState.Established) {
      setupRemoteAudio(s);
      if (st === SessionState.Established) {
        stopRingAlert();
        usePhoneStore.setState({ status: 'in-call', startedAt: Date.now() });
      }
    } else if (st === SessionState.Terminating || st === SessionState.Terminated) {
      cleanupSession();
    }
  });
  const remote = parseUser(s);
  usePhoneStore.setState((p) => ({
    ...p,
    status: direction === 'in' ? 'ringing-in' : 'ringing-out',
    remoteNumber: remote,
    direction,
    muted: false,
    held: false,
    startedAt: undefined,
    customer: null,
    // 换一通：上一通的客户身份（含收号/核验解析出的客户号码）作废，先按对端号弹屏
    customerPhone: '',
    displayName: '',
    transferredOut: false,
    consultPhase: '',
    consultDest: '',
  }));
  // 来电弹屏/外呼客户匹配（异步，命中后写入 customer）
  void matchCustomer(remote);
  // 小结定位基线（必须在挂断前完成快照，cleanupSession 保留至弹窗读取）
  void captureCdrBaseline();
  // 来电提醒：振铃音 + 标题闪烁 + 系统通知（接听/挂断/对方取消时停止）
  if (direction === 'in') {
    rt.lastOutboundDial = '';
    rt.activeOutboundSeq = 0;
    rt.manualHangup = false;
    startRingAlert(remote);
  }
}

/** 拆除现有 UA（unregister + stop），供 register 换号/注销复用。
 *  注意：先清空模块级引用再清理资源——即使底层 unregister/stop 挂起，
 *  模块状态也已复位，后续 register 可新建 UA（实测 480 后 teardown 会挂起卡死）。 */
export async function teardown() {
  const s = rt.session;
  const oldReg = rt.registerer;
  const oldUa = rt.ua;
  rt.session = null;
  rt.registerer = null;
  rt.ua = null;
  if (s && s.state !== SessionState.Terminated) {
    try {
      if (s.state === SessionState.Established) await s.bye();
      else if (s instanceof Invitation) await s.reject();
      else if (s instanceof Inviter) await s.cancel();
    } catch {
      /* 已结束 */
    }
  }
  try {
    await oldReg?.unregister();
  } catch {
    /* noop */
  }
  try {
    await oldUa?.stop();
  } catch {
    /* noop */
  }
}
